import React, { type ComponentProps, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Card, Empty, Skeleton } from 'antd';
import Datestamp from '../Datestamp';
import { useQuery } from '@tanstack/react-query';
import useApi from '../../hooks/useApi';

type ApptDetails = {
  appt_id: number;
  appointment_type: string;
  clinician_name: string;
  clinician_subtype_desc: string;
  appt_date: Date | null;
  appt_completed_date: Date | null;
  notes: {
    note_code: string;
    note_desc: string;
    note_text: string | null;
  }[];
};

const FlexItem = styled.div<{ flexBasis?: CSSProperties['flexBasis'] }>`
  flex-basis: ${({ flexBasis }) => flexBasis || 'unset'};

  strong {
    display: block;
    opacity: 0.5;
    font-size: 0.8rem;
    font-weight: 500;
  }

  span {
    display: block;
  }
`;

type ItemProps = {
  label: string;
  children: ReactNode;
} & ComponentProps<typeof FlexItem>;

const Item = ({ label, children, flexBasis }: ItemProps) => (
  <FlexItem {...{ flexBasis }}>
    <strong>{label}</strong> <span>{children}</span>
  </FlexItem>
);

type FlexWrapProps = {
  density: 'sm' | 'md';
};

const densityGaps: Record<FlexWrapProps['density'], CSSProperties['gap']> = {
  sm: '1em',
  md: '1em 2em;'
};

const FlexWrap = styled.div<FlexWrapProps>`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ density }: FlexWrapProps) => densityGaps[density]};
`;

const StyledCard = styled(Card)`
  margin: 0 0 1em;
`;

const RichText = styled.div`
  font-size: 0.95rem;

  p {
    margin: 0;
  }
`;

type ClinicalNoteHistoryProps = {
  apptId: string;
};

export const ClinicalNoteHistory = ({ apptId }: ClinicalNoteHistoryProps) => {
  const { t } = useTranslation('common');
  const { Api } = useApi();

  const {
    data: appointments = [],
    isPending,
    isError,
    error
  } = useQuery<ApptDetails[]>({
    queryKey: ['appointmentDetails', apptId],
    queryFn: async () => {
      const response = await Api.clinicians.getAppointmentDetails(apptId);
      return response.data.appts || [];
    },
    enabled: !!apptId
  });

  if (!apptId) {
    return null;
  }

  if (isPending) {
    return (
      <StyledCard>
        <Skeleton />
      </StyledCard>
    );
  }

  if (isError) {
    return (
      <StyledCard>
        <Empty description={error?.toString() || 'Error loading clinical notes'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </StyledCard>
    );
  }

  const appointmentsWithNonEmptyNotes = appointments.filter((appointment) => !!appointment.notes.length);

  if (!appointmentsWithNonEmptyNotes.length) {
    return (
      <StyledCard>
        <Empty description={t('No clinical notes found')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </StyledCard>
    );
  }

  return (
    <div data-testid="clinician-notes-history">
      {appointmentsWithNonEmptyNotes?.map(({ notes, ...appointment }) => {
        const appointmentDatetime = appointment.appt_date || appointment.appt_completed_date;

        return (
          <StyledCard
            key={`${appointment.appt_id}-${appointmentDatetime}`}
            title={
              <FlexWrap density="md">
                <Item label={t('Appointment ID')}>{appointment.appt_id}</Item>
                <Item label={t('Clinician name')}>{appointment.clinician_name}</Item>
                <Item label={t('Clinician subtype')}>{appointment.clinician_subtype_desc}</Item>
                <Item label={t('Appointment type')}>{appointment.appointment_type}</Item>
                {appointmentDatetime && (
                  <Item label={t('Date of appointment')}>
                    <Datestamp date={appointmentDatetime} format="Date @ Time - 03/01/2022 @ 08:57" />
                  </Item>
                )}
              </FlexWrap>
            }
            size="small"
          >
            <FlexWrap density="sm">
              {notes.map(({ note_code, note_text, note_desc }) => (
                <Item key={note_code} label={note_desc} flexBasis="calc(50% - 1em)">
                  {note_text ? <RichText dangerouslySetInnerHTML={{ __html: note_text }} /> : t('No notes')}
                </Item>
              ))}
            </FlexWrap>
          </StyledCard>
        );
      })}
    </div>
  );
};
